import * as DahDocumentsActions from "./dah-documents.actions";

import {createEntityAdapter, EntityAdapter} from "@ngrx/entity";
import {createReducer, on} from "@ngrx/store";
import {StoredComponentHelperService} from '@common/helpers/stored-component.helper.service';
import {DahDocument, DahDocumentSearchParams} from '@documents/_common/models/dah-documents.models';
import {CategoryListResponse} from '@common/components-standalone/reference-category-module/category.type';
import {CategorizedEntityStateModel} from '@common/models/entity-state.models';
import {SetAllInitialStates} from '@common/store/actions/core.actions';

export const dahDocumentFeatureKey = 'dahDocument';

enum DocumentTypeSorting {'ACT', 'INVOICE', 'AGREEMENT'}

export function sortEntities(a: DahDocument, b: DahDocument) {
    if (a.date == b.date) {
        return DocumentTypeSorting[a.type!] > DocumentTypeSorting[b.type!] ? 1 : -1;
    }

    return a.date! < b.date! ? 1 : -1;
}

export const adapter: EntityAdapter<DahDocument> = createEntityAdapter<DahDocument>({
    sortComparer: sortEntities
});

export const categoriesAdapter: EntityAdapter<CategoryListResponse> = createEntityAdapter<CategoryListResponse>({
    selectId: (category) => category.category.id!
});

export type DahDocumentState = CategorizedEntityStateModel<DahDocument, DahDocumentSearchParams>;

export const initialState: DahDocumentState = adapter.getInitialState({
    listLoading: 'listLoading',
    pagination: {},
    itemsLoading: [],
    entityCategories: categoriesAdapter.getInitialState(),
    categoriesTreeState: {
        opened: {'default': true}
    }
});

export const dahDocumentReducer = createReducer(
    initialState,

    on(DahDocumentsActions.SetCategoriesState, (state, action) => ({
        ...state,
        categoriesTreeState: action.payload
    })),

    on(DahDocumentsActions.LoadCategoriesSuccess, (state, action) => ({
        ...state,
        entityCategories: categoriesAdapter.setAll(action.payload, {...state.entityCategories})
    })),

    on(DahDocumentsActions.CheckState, (state, action) => {
        if (action.payload.close) {
            return adapter.setAll(action.payload.items, {
                ...state,
                pagination: {...state.pagination, number: 0},
                componentOpened: false
            });
        }

        return {...state, componentOpened: true};
    }),

    on(DahDocumentsActions.LoadList, (state, action) => ({
        ...state,
        listLoading: StoredComponentHelperService.updateListLoading(action.payload, state.ids)
    })),

    on(DahDocumentsActions.LoadListSuccess, (state, action) => {
        return action.payload.clearState
            ? adapter.setAll(action.payload.items, {
                ...state,
                pagination: action.payload.pagination,
                listLoading: undefined

            })
            : adapter.upsertMany(action.payload.items, {
                ...state,
                pagination: action.payload.pagination,
                listLoading: undefined
            });
    }),

    on(DahDocumentsActions.LoadListFailure, (state) => ({
        ...state,
        listLoading: undefined
    })),

    on(DahDocumentsActions.SetFilter, (state, action) => ({
        ...state,
        filter: action.payload
    })),

    on(DahDocumentsActions.SetPagination, (state, action) => ({
        ...state,
        pagination: {...state.pagination, number: action.payload ? StoredComponentHelperService.setNextPage(state.pagination, state.ids?.length) : 0}
    })),

    on(DahDocumentsActions.SetLoading, (state, action) => ({
        ...state,
        listLoading: action.payload
    })),

    on(DahDocumentsActions.SetItemsLoading, (state, action) => ({
        ...state,
        itemsLoading: StoredComponentHelperService.updateItemLoading(state.itemsLoading, action.payload.id, action.payload.isStart)
    })),

    on(DahDocumentsActions.UpdateItemSuccess, (state, action) => {
        return adapter.setOne(action.payload!, {
            ...state,
            itemsLoading: StoredComponentHelperService.updateItemLoading(state.itemsLoading, action.payload.id!, false),
            pagination: {...state.pagination, totalElements: StoredComponentHelperService.setTotalElements(action.payload.id!, state.pagination, state?.ids)}
        });
    }),

    on(DahDocumentsActions.SetInitialState, SetAllInitialStates, () => ({...initialState}))
);

export const {selectAll: selectAllEntities} = adapter.getSelectors();
export const {selectAll: selectAllCategories} = categoriesAdapter.getSelectors((state: DahDocumentState) => state.entityCategories);
